import { useEffect, useState } from "react";
import { Card, Col, Row, Spinner } from "react-bootstrap";
import { AgCharts } from "ag-charts-react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChartLine } from "@fortawesome/free-solid-svg-icons";
import { ClientService } from "../../component/services/client.service";
import { makeParams } from "../../component/api/makeRequest";
import { DateRange } from "../../component/DateRangeSelector/DateRangeSelector";
import { useTheme } from "../../theme/useTheme";
import moment from "moment";

interface DayEntry { day: string; success: number; failure: number; }
interface VendorTrend { vendorName: string; days: DayEntry[]; }

interface Props { dateRange: DateRange; }

const VENDOR_COLORS: Record<string, { success: string; failure: string }> = {
    CIPL:   { success: "#10b981", failure: "#ef4444" },
    BRIGHT: { success: "#3b82f6", failure: "#f59e0b" },
    sai:    { success: "#8b5cf6", failure: "#ec4899" },
};
const FALLBACK = ["#06b6d4","#f97316","#84cc16","#a78bfa"];

function vendorColor(name: string, idx: number) {
    return VENDOR_COLORS[name] ?? {
        success: FALLBACK[idx % FALLBACK.length],
        failure: FALLBACK[(idx + 2) % FALLBACK.length],
    };
}

export default function VendorDailyTrend({ dateRange }: Props) {
    const { colors, colorSchemes } = useTheme();
    const [data, setData] = useState<VendorTrend[]>([]);
    const [loading, setLoading] = useState(false);

    async function fetchData(start: Date, end: Date) {
        setLoading(true);
        setData([]);
        const params = makeParams([
            { index: "start_time", value: moment(start).toISOString() },
            { index: "end_time", value: moment(end).toISOString() },
        ]);
        await ClientService.getVendorDailyTrend(params)
            .then((res: any) => setData(res.data?.data || []))
            .catch((err: any) => console.log(err))
            .finally(() => setLoading(false));
    }

    useEffect(() => {
        fetchData(dateRange.startDate, dateRange.endDate);
    }, [dateRange.startDate, dateRange.endDate]);

    // Build a unified day list across all vendors, then pivot into one flat array per vendor
    const allDays = Array.from(
        new Set(data.flatMap(v => v.days.map(d => d.day)))
    ).sort();

    // One chart per vendor so lines stay readable
    const charts = data.map((vendor, idx) => {
        const dayMap = new Map(vendor.days.map(d => [d.day, d]));
        const chartData = allDays.map(day => ({
            date: new Date(day),
            success: dayMap.get(day)?.success ?? 0,
            failure: dayMap.get(day)?.failure ?? 0,
        }));
        const vc = vendorColor(vendor.vendorName, idx);

        const options: any = {
            data: chartData,
            height: 220,
            background: { fill: "transparent" },
            padding: { top: 12, right: 16, bottom: 8, left: 8 },
            axes: [
                {
                    type: "time",
                    position: "bottom",
                    label: {
                        format: "%d %b",
                        fontSize: 10,
                        color: colors.text.muted,
                    },
                    gridLine: { style: [{ stroke: colors.border.light, lineDash: [4, 4] }] },
                },
                {
                    type: "number",
                    position: "left",
                    label: { fontSize: 10, color: colors.text.muted },
                    gridLine: { style: [{ stroke: colors.border.light, lineDash: [4, 4] }] },
                },
            ],
            series: [
                {
                    type: "line",
                    xKey: "date",
                    yKey: "success",
                    yName: "Success",
                    stroke: vc.success,
                    strokeWidth: 2.5,
                    marker: { enabled: true, size: 5, fill: vc.success, stroke: "#fff", strokeWidth: 1.5 },
                    tooltip: {
                        renderer: (p: any) => ({
                            content: `${moment(p.datum.date).format("DD MMM")}: ${p.datum.success} success`,
                        }),
                    },
                },
                {
                    type: "line",
                    xKey: "date",
                    yKey: "failure",
                    yName: "Failure",
                    stroke: vc.failure,
                    strokeWidth: 2.5,
                    lineDash: [6, 3],
                    marker: { enabled: true, size: 5, fill: vc.failure, stroke: "#fff", strokeWidth: 1.5 },
                    tooltip: {
                        renderer: (p: any) => ({
                            content: `${moment(p.datum.date).format("DD MMM")}: ${p.datum.failure} failure`,
                        }),
                    },
                },
            ],
            legend: {
                enabled: true,
                position: "bottom",
                item: {
                    marker: { size: 8 },
                    label: { fontSize: 11, color: colors.text.secondary },
                },
            },
        };

        return { vendor, options, vc };
    });

    return (
        <div className="mt-4 mb-4">
            <div className="mb-3">
                <h6 style={{ fontWeight: 700, color: colors.text.primary, fontSize: "1rem", marginBottom: 2 }}>
                    Failure Trend Over Time
                </h6>
                <span style={{ color: colors.text.muted, fontSize: "0.78rem" }}>
                    Daily distinct success vs failure per vendor
                </span>
            </div>

            {loading ? (
                <div className="d-flex align-items-center justify-content-center py-5">
                    <Spinner animation="border" style={{ color: colors.primary.main }} />
                </div>
            ) : data.length === 0 ? (
                <div className="d-flex flex-column align-items-center justify-content-center py-5">
                    <FontAwesomeIcon icon={faChartLine} style={{ fontSize: "2.5rem", color: colors.border.secondary, marginBottom: "1rem" }} />
                    <span style={{ color: colors.text.muted, fontSize: "0.9rem" }}>No trend data for the selected range</span>
                </div>
            ) : (
                <Row className="g-3">
                    {charts.map(({ vendor, options, vc }) => (
                        <Col key={vendor.vendorName} md={6} xl={4}>
                            <Card style={{ ...colorSchemes.card, borderRadius: "14px", border: `1px solid ${colors.border.light}`, overflow: "hidden" }}>
                                <div
                                    className="d-flex align-items-center gap-2 px-4 py-3"
                                    style={{ borderBottom: `1px solid ${colors.border.light}`, background: colors.background.secondary }}
                                >
                                    <div style={{ display: "flex", gap: 6 }}>
                                        <div style={{ width: 12, height: 12, borderRadius: "50%", background: vc.success, marginTop: 2 }} />
                                        <div style={{ width: 12, height: 12, borderRadius: "50%", background: vc.failure, marginTop: 2 }} />
                                    </div>
                                    <span style={{ fontWeight: 700, fontSize: "0.9rem", color: colors.text.primary }}>
                                        {vendor.vendorName}
                                    </span>
                                </div>
                                <div className="p-2">
                                    <AgCharts options={options} />
                                </div>
                            </Card>
                        </Col>
                    ))}
                </Row>
            )}
        </div>
    );
}
